// client/src/pages/secure/SiteSearchAlertsPage.js — Site search → Alert emails.
//
// LoopNet / Crexi saved-search alerts are sent to our own receive-only address and filed
// into the matching search by the listener on the server. This page shows whether that
// listener is running, its settings (managing broker), the alerts that need a person
// ("Unmatched alerts": pick the search), and other mail from the listing sites such as
// account-verification emails, shown as plain text with their links. Nothing here
// fetches a link: a link opens in the person's own browser only when they click it.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const DOT = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', grey: 'bg-gray-400' };
const OUTCOME = {
    filed: 'Filed', unmatched: 'Needs a search', error: 'Filing failed', no_listings: 'No listings read',
    other: 'Other mail', ignored: 'Ignored', dismissed: 'Dismissed',
};
const SITE = { loopnet: 'LoopNet', crexi: 'Crexi', cityfeet: 'CityFeet' };
const when = (d) => (d ? new Date(d).toLocaleString() : '—');
const hostOf = (v) => String(v || '').toLowerCase().split('@').pop();

const Card = ({ title, children, right }) => (
    <section className="rounded-lg bg-white p-5 shadow-md">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
            {right}
        </div>
        {children}
    </section>
);

const SourceTag = ({ s }) => (s && s !== 'portal'
    ? <span className="ml-1 text-[11px] text-slate-500">({s === 'server' ? 'from the server file' : 'default'})</span> : null);

const Settings = ({ data, onSaved }) => {
    const apiClient = useApiClient();
    const s = data.settings;
    const [form, setForm] = useState(null);
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setForm({
            enabled: !!s.enabled, recipient: s.recipient || '', sender_domains: (s.sender_domains || []).join(', '),
            listen_port: String(s.listen_port || ''), max_message_kb: String(s.max_message_kb || ''),
        });
    }, [s]);
    if (!form) return null;
    const editable = data.can_edit_settings;
    const save = async (patch) => {
        setSaving(true); setMsg(null);
        try {
            const body = patch || { ...form, listen_port: form.listen_port || null, max_message_kb: form.max_message_kb || null };
            const r = await apiClient.put('/api/site-search-alerts/settings', body);
            setMsg({ ok: true, text: r.message });
            onSaved();
        } catch (e) { setMsg({ ok: false, text: e.message }); }
        setSaving(false);
    };
    const rejects = (data.listener && data.listener.recent_rejects) || [];
    const allow = (value) => {
        const d = hostOf(value);
        if (!d || !window.confirm(`Accept mail from ${d} (and its subdomains)? Only do this if it is a listing site's mail server.`)) return;
        save({ sender_domains: [...new Set([...(s.sender_domains || []), d])] });
    };
    const input = 'mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-50';
    return (
        <Card title="Settings" right={!editable && <span className="text-xs text-slate-500">Changed by the managing broker</span>}>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={form.enabled} disabled={!editable}
                       onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                Accept alert emails <SourceTag s={s.sources.enabled} />
            </label>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-sm text-slate-700">Receiving address <SourceTag s={s.sources.recipient} />
                    <input className={input} value={form.recipient} disabled={!editable} placeholder={s.suggested_recipient}
                           onChange={(e) => setForm({ ...form, recipient: e.target.value })} />
                    <span className="text-[11px] text-slate-500">Register this address as the account email on LoopNet and Crexi. Only mail to exactly this address is accepted.</span>
                </label>
                <label className="text-sm text-slate-700">Allowed sender domains <SourceTag s={s.sources.sender_domains} />
                    <textarea className={input} rows={2} value={form.sender_domains} disabled={!editable}
                              onChange={(e) => setForm({ ...form, sender_domains: e.target.value })} />
                    <span className="text-[11px] text-slate-500">Comma-separated. Subdomains are included. Built-in list: {s.default_sender_domains.join(', ')}.</span>
                </label>
                <label className="text-sm text-slate-700">Listening port <SourceTag s={s.sources.listen_port} />
                    <input className={input} value={form.listen_port} disabled={!editable} inputMode="numeric"
                           onChange={(e) => setForm({ ...form, listen_port: e.target.value })} />
                    <span className="text-[11px] text-slate-500">2525 unless told otherwise; the server's firewall passes port 25 to it.</span>
                </label>
                <label className="text-sm text-slate-700">Largest message (KB) <SourceTag s={s.sources.max_message_kb} />
                    <input className={input} value={form.max_message_kb} disabled={!editable} inputMode="numeric"
                           onChange={(e) => setForm({ ...form, max_message_kb: e.target.value })} />
                </label>
            </div>
            {msg && <p className={`mt-3 rounded px-3 py-2 text-sm ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{msg.text}</p>}
            {editable && (
                <div className="mt-3 flex justify-end">
                    <button type="button" onClick={() => save()} disabled={saving}
                            className="rounded bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-900 disabled:bg-indigo-300">
                        {saving ? 'Saving…' : 'Save settings'}
                    </button>
                </div>
            )}
            {rejects.length > 0 && (
                <div className="mt-4">
                    <h3 className="text-sm font-semibold text-slate-700">Recently refused</h3>
                    <p className="text-xs text-slate-500">If a listing site's alert was refused because of its sender domain, allow that domain here.</p>
                    <ul className="mt-1 divide-y divide-slate-100 text-sm">
                        {rejects.map((r, i) => (
                            <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-1">
                                <span><b>{r.value || r.ip}</b> — {r.reason} ({r.count}×, last {when(r.last_at)})</span>
                                {editable && r.stage === 'mail' && r.reason === 'sender domain not allowed' && (
                                    <button type="button" onClick={() => allow(r.value)} className="text-xs text-indigo-700 hover:underline">Allow {hostOf(r.value)}</button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Card>
    );
};

const downloadOriginal = async (id) => {
    const r = await fetch(`/api/site-search-alerts/${id}/original`, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
    if (!r.ok) { let m = 'Could not download.'; try { m = (await r.json()).message || m; } catch { /* ignore */ } window.alert(m); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a'); a.href = url; a.download = `alert-${id}.eml`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// One message opened: listings, text, links. Plain text only — no HTML from mail is rendered.
const MessageDetail = ({ id, searches, onDone }) => {
    const apiClient = useApiClient();
    const [a, setA] = useState(null);
    const [pick, setPick] = useState('');
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { apiClient.get(`/api/site-search-alerts/${id}`).then(setA).catch((e) => setMsg({ ok: false, text: e.message })); }, [apiClient, id]);
    if (!a) return msg ? <p className="text-sm text-red-700">{msg.text}</p> : <p className="text-sm text-slate-500">Loading…</p>;
    const act = async (fn) => {
        setBusy(true); setMsg(null);
        try { const r = await fn(); setMsg({ ok: true, text: r.message || 'Done.' }); onDone(); }
        catch (e) { setMsg({ ok: false, text: e.message }); }
        setBusy(false);
    };
    const fileable = ['unmatched', 'error', 'no_listings', 'dismissed'].includes(a.outcome) && a.listings.length > 0;
    return (
        <div className="mt-2 space-y-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="text-xs text-slate-600">From {a.from_address || '—'} · received {when(a.received_at)}{a.saved_search_name ? ` · alert "${a.saved_search_name}"` : ''}</p>
            {a.listings.length > 0 && (
                <div className="overflow-x-auto">
                    <DataList rows={a.listings.map((l, i) => ({ ...l, _key: i }))} rowKey="_key" csvName="alert-listings" defaultSort={{ key: 'municipality', dir: 'asc' }}
                        columns={[
                            { key: 'address', label: 'Address' },
                            { key: 'municipality', label: 'Town' },
                            { key: 'size_sf', label: 'SF', type: 'number' },
                            { key: 'rate', label: 'Rate / price' },
                            { key: 'type', label: 'Type', value: (l) => [l.listing_type, l.property_type].filter(Boolean).join(' · ') },
                            { key: 'source_url', label: 'Listing', sortable: false, search: false, render: (l) => l.source_url && <a href={l.source_url} target="_blank" rel="noopener noreferrer nofollow" className="text-indigo-700 hover:underline">{SITE[l.source] || 'open'}</a> },
                        ]} />
                </div>
            )}
            {fileable && (
                <div className="flex flex-wrap items-center gap-2">
                    <select value={pick} onChange={(e) => setPick(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm">
                        <option value="">Pick the search…</option>
                        {(searches || []).map((s) => <option key={s.search_id} value={s.search_id}>BREC-{s.search_id} · {s.client_name} · {s.title}</option>)}
                    </select>
                    <button type="button" disabled={!pick || busy} className="rounded bg-slate-800 px-3 py-1 text-white disabled:bg-indigo-300"
                            onClick={() => act(() => apiClient.post(`/api/site-search-alerts/${id}/file`, { search_id: Number(pick) }))}>File into this search</button>
                </div>
            )}
            {a.links.length > 0 && (
                <div>
                    <h4 className="text-xs font-semibold uppercase text-slate-500">Links in this email</h4>
                    <p className="text-[11px] text-slate-500">The portal never opens these. Clicking one opens it in your own browser — check the address is the site you expect.</p>
                    <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto">
                        {a.links.map((l, i) => (
                            <li key={i} className="truncate">
                                <a href={l.href} target="_blank" rel="noopener noreferrer nofollow" className="text-indigo-700 hover:underline">{l.text}</a>
                                <span className="ml-1 text-[11px] text-slate-400">{hostOf(l.href.replace(/^https?:\/\//, '').split('/')[0])}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {a.body_text && (
                <details>
                    <summary className="cursor-pointer text-xs font-semibold text-slate-600">The email as text</summary>
                    <pre className="mt-1 max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded bg-white p-2 text-xs text-slate-700">{a.body_text}</pre>
                </details>
            )}
            <div className="flex flex-wrap gap-3 text-xs">
                {!['filed', 'dismissed'].includes(a.outcome) && (
                    <button type="button" disabled={busy} className="text-slate-600 hover:underline"
                            onClick={() => act(() => apiClient.post(`/api/site-search-alerts/${id}/dismiss`, {}).then(() => ({ message: 'Dismissed.' })))}>Dismiss</button>
                )}
                {a.has_original && <button type="button" className="text-slate-600 hover:underline" onClick={() => downloadOriginal(id)}>Download original (.eml)</button>}
            </div>
            {msg && <p className={`rounded px-2 py-1 ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{msg.text}</p>}
        </div>
    );
};

// One list per section; click a row to open the message below the list.
const MessageList = ({ rows, empty, searches, onDone, showSearch }) => {
    const [open, setOpen] = useState(null);
    return (
        <div>
            <DataList rows={rows} rowKey="alert_id" csvName="alert-emails" empty={empty} defaultSort={{ key: 'received_at', dir: 'desc' }}
                onRowClick={(m) => setOpen(open === m.alert_id ? null : m.alert_id)}
                rowClass={(m) => (open === m.alert_id ? 'bg-indigo-50' : '')}
                filters={[{ key: 'outcome', label: 'Outcome' }, { key: 'source', label: 'From' }]}
                columns={[
                    { key: 'subject', label: 'Subject', value: (m) => m.subject || '(no subject)', render: (m) => <span className="font-medium text-slate-800">{m.subject || '(no subject)'}</span> },
                    { key: 'source', label: 'From', value: (m) => SITE[m.source] || m.from_address },
                    { key: 'received_at', label: 'Received', type: 'date', render: (m) => <span className="text-xs text-slate-500 whitespace-nowrap">{when(m.received_at)}</span> },
                    { key: 'outcome', label: 'Outcome', value: (m) => OUTCOME[m.outcome] || m.outcome, render: (m) => <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold">{OUTCOME[m.outcome] || m.outcome}</span> },
                    ...(showSearch ? [{ key: 'search', label: 'Search', value: (m) => (m.search_id ? `BREC-${m.search_id}${m.search_title ? ` ${m.search_title}` : ''}` : '') }] : []),
                    { key: 'listings_found', label: 'Listings', type: 'number' },
                    { key: 'detail', label: 'Detail', render: (m) => <span className="text-xs text-slate-600">{m.detail}</span> },
                ]} />
            {open && rows.some((m) => m.alert_id === open) && <MessageDetail id={open} searches={searches} onDone={onDone} />}
        </div>
    );
};

const SiteSearchAlertsPage = () => {
    const apiClient = useApiClient();
    const [data, setData] = useState(null);
    const [searches, setSearches] = useState([]);
    const [error, setError] = useState('');
    const load = useCallback(() => {
        apiClient.get('/api/site-search-alerts').then((d) => { setData(d); setError(''); }).catch((e) => setError(e.message || 'Could not load.'));
    }, [apiClient]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        apiClient.get('/api/site-searches').then((rows) => setSearches(rows.filter((s) => s.status !== 'closed'))).catch(() => setSearches([]));
    }, [apiClient]);

    if (error && !data) return <div className="p-8 text-slate-600">{error}</div>;
    if (!data) return <div className="p-8">Loading…</div>;
    const c = data.counts_30d || {};
    return (
        <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Alert emails</h1>
                <p className="text-sm text-slate-600">
                    LoopNet and Crexi saved-search alerts, filed into the client's site search by the alert's name
                    ({'"BREC-<number> …"'}). Each search shows its alert name at the top of the search.
                </p>
            </div>
            {!data.tables_present && <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">Not installed on this server yet (database migration 2026_10_06_alert_mail.sql).</p>}
            <Card title="Status" right={<Link to="/dashboard/system-status" className="text-xs text-indigo-700 hover:underline">System status</Link>}>
                <p className="flex items-center gap-2 text-sm text-slate-800">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${DOT[data.health.tone] || DOT.grey}`} />{data.health.text}
                </p>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                    <div><dt className="text-xs text-slate-500">Transport</dt><dd>Our own mail address (receive-only)</dd></div>
                    <div><dt className="text-xs text-slate-500">Last mail received</dt><dd>{when(data.last_received_at)}</dd></div>
                    <div><dt className="text-xs text-slate-500">Listener last checked in</dt><dd>{when(data.listener && data.listener.last_seen_at)}</dd></div>
                    <div><dt className="text-xs text-slate-500">Secure connection</dt><dd className="text-xs">{(data.listener && data.listener.tls_status) || '—'}</dd></div>
                </dl>
                <p className="mt-2 text-xs text-slate-600">Last 30 days: {c.filed || 0} filed · {(c.unmatched || 0) + (c.error || 0) + (c.no_listings || 0)} needed a person · {c.other || 0} other mail · {c.ignored || 0} ignored · {c.dismissed || 0} dismissed</p>
            </Card>
            <Card title={`Unmatched alerts (${data.unmatched.length})`}>
                <MessageList rows={data.unmatched} searches={searches} onDone={load} empty="Nothing waiting. Every alert found its search." />
            </Card>
            <Card title={`Other mail from listing sites (${data.other.length})`}>
                <p className="mb-2 text-xs text-slate-500">Account verification and other notices from LoopNet, Crexi or CityFeet. Open one to see its links.</p>
                <MessageList rows={data.other} searches={searches} onDone={load} empty="None." />
            </Card>
            <Card title="Recently filed">
                <MessageList rows={data.recent} searches={searches} onDone={load} showSearch empty="No alert filed yet." />
            </Card>
            <Settings data={data} onSaved={load} />
        </div>
    );
};

export default SiteSearchAlertsPage;
